import { useBoxControlsContext } from "@/utils/box-controls-context";
import { BoxControls as LotvBoxControls } from "@faro-lotv/lotv";
import { useCallback, useState } from "react";
import { Box3, Matrix4, Matrix4Tuple, Plane } from "three";
import { OBB } from "three-stdlib";

const TEMP_OBB = new OBB();

/** The state that holds the box controls must support both null and undefined types. */
export type BoxControlsViaRef = LotvBoxControls | null | undefined;

type ClippingPlanesChangedCbk = (planes: Plane[]) => void;

type ManageClippingPlanesChangeRet = {
  /** Callback issued when the clipping planes change */
  onClippingPlanesChanged: ClippingPlanesChangedCbk;
  /** The clipping planes for the preview */
  clippingPlanesPreview: Plane[] | undefined;
};

/**
 * Returns a callback to handle the event of clipping planes changing
 *
 * @param modelBB The point cloud bounding box
 * @param worldMatrix The point cloud world pose
 * @param boxControls The box controls
 * @returns A callback to handle the event of clipping planes changing
 */
export function useManageClippingPlanesChange(
  modelBB: Box3,
  worldMatrix: Matrix4Tuple,
  boxControls: BoxControlsViaRef,
): ManageClippingPlanesChangeRet {
  const [clippingPlanesPreview, setClippingPlanesPreview] = useState<Plane[]>();
  const { setClippingPlanes } = useBoxControlsContext();

  const onClippingPlanesChanged = useCallback(
    (clippingPlanes: Plane[]) => {
      // The clipping planes for the preview should always be defined and in world space
      setClippingPlanesPreview(clippingPlanes);

      if (!boxControls) return;
      // Compute an OBB (Oriented Bounding Box) to check if the user defined box is valid and intersect the PC box
      const trx = boxControls.boxMatrixWorld;
      TEMP_OBB.center.set(0, 0, 0);
      TEMP_OBB.halfSize.set(0.5, 0.5, 0.5);
      TEMP_OBB.rotation.identity();
      TEMP_OBB.applyMatrix4(trx);
      // If any of the obb halfSize is 0.01 then the box is too small
      // 0.01 is the distance at which two box handle will overlap
      const MIN_SIZE = 0.01;
      if (
        TEMP_OBB.halfSize.x < MIN_SIZE ||
        TEMP_OBB.halfSize.y < MIN_SIZE ||
        TEMP_OBB.halfSize.z < MIN_SIZE
      ) {
        setClippingPlanes(undefined);
        return;
      }
      const hit = TEMP_OBB.intersectsBox3(modelBB);
      // If there's no intersection we don't have a valid set of planes to extract a volume
      if (!hit) {
        setClippingPlanes(undefined);
        return;
      }

      const invModelTransform = new Matrix4().fromArray(worldMatrix).invert();
      const planes = clippingPlanes.map((plane) =>
        plane.clone().applyMatrix4(invModelTransform),
      );
      setClippingPlanes(planes);
    },
    [modelBB, worldMatrix, setClippingPlanes, boxControls],
  );

  return {
    onClippingPlanesChanged,
    clippingPlanesPreview,
  };
}
